import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { articles } from "../../pages/Blog";
import { Helmet } from "react-helmet";

import "prismjs/themes/prism-tomorrow.css";
import Prism from "prismjs";
import "prismjs/components/prism-solidity";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";

// Add dummy support for 'move' if not available in Prism default, or map it to rust-like if appropriate. 
// However, Prism might not have 'move'. We can alias it or just let it be.
// Extropy often works with ZK/Starknet so Cairo might be relevant too, but user asked specifically about Solidity and implied the existing tags.
// Let's add rust as a fallback for move if needed, or just standard load.
import "prismjs/components/prism-rust";

// Alias 'move' to 'rust' since PrismJS doesn't have a default Move component
// and Move is syntactically similar to Rust.
if (Prism.languages.rust) {
    Prism.languages.move = Prism.languages.rust;
}

const ArticleTemplate = ({
    title,
    subtitle,
    author,
    date,
    readingTime,
    tags = [],
    children,
    canonicalUrl,
    contentHtml,

}) => {
    const [relatedArticles, setRelatedArticles] = useState([]);

    useEffect(() => {
        Prism.highlightAll();
    }, [contentHtml, children]);


    useEffect(() => {
        // SMART RELATED ARTICLES SYSTEM
        // Find articles with matching tags
        const currentTags = tags.map((tag) => tag.toLowerCase());
        const currentTitle = title; // Use title to exclude current article

        const related = articles
            .filter((article) => article.title !== currentTitle) // Don't show current article
            .map((article) => {
                // Calculate relevance score based on matching tags
                const articleTags = article.tags.map((tag) => tag.toLowerCase());
                const matchingTags = currentTags.filter((tag) =>
                    articleTags.includes(tag)
                );
                return {
                    ...article,
                    relevanceScore: matchingTags.length,
                };
            })
            .filter((article) => article.relevanceScore > 0) // Only show articles with matching tags
            .sort((a, b) => b.relevanceScore - a.relevanceScore) // Sort by relevance
            .slice(0, 3); // Show max 3 related articles

        setRelatedArticles(related);
    }, [tags, title]);

    const shareOnTwitter = () => {
        const text = title;
        const url = window.location.href;
        window.open(
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                text
            )}&url=${encodeURIComponent(url)}`,
            "_blank"
        );
    };

    const shareOnLinkedIn = () => {
        const url = window.location.href;
        window.open(
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                url
            )}`,
            "_blank"
        );
    };

    return (
        <div className="article-body">
            {/* Article Header */}
            {canonicalUrl && (
                <Helmet>
                    <link rel="canonical" href={canonicalUrl} />
                    <title>{title} | Extropy Security</title>
                    {subtitle && <meta name="description" content={subtitle} />}
                </Helmet>
            )}
            <section className="article-header">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto">
                            <div className="article-meta">
                                <span>
                                    <i className="fas fa-calendar-alt"></i> {date}
                                </span>
                                <span>
                                    <i className="fas fa-user"></i> by {author}
                                </span>
                                <span>
                                    <i className="fas fa-clock"></i> {readingTime}
                                </span>
                            </div>

                            <h1 className="display-4 mb-3">{title}</h1>
                            <p className="lead">{subtitle}</p>

                            <div className="article-tags">
                                {tags.map((tag, index) => (
                                    <span key={index} className="article-tag">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Article Content */}
            <article className="article-content">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-8 mx-auto">
                            {/* Main Content */}
                            {children ? (
                                children
                            ) : (
                                contentHtml && (
                                    <div
                                        className="article-markdown"
                                        dangerouslySetInnerHTML={{ __html: contentHtml }}
                                    />
                                )
                            )}

                            {/* CTA Section */}
                            <div className="cta-section mt-4 text-center">
                                <h4 className="mb-3">Ready to ship safer smart contracts?</h4>
                                <p className="mb-0">
                                    If your protocol is preparing for launch, don&apos;t just check for known bugs.
                                    Get an audit that uncovers the unknown.
                                </p>
                                <div className="cta-actions d-flex flex-column flex-sm-row flex-md-row align-items-stretch justify-content-center gap-3 mt-4">
                                    <a
                                        href="mailto:audits@extropy.io?subject=Audit%20Inquiry%20from%20Article"
                                        className="btn btn-cta"
                                    >
                                        Request an Audit
                                    </a>
                                    <a
                                        href="https://security.extropy.io/audits"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn btn-outline-secondary"
                                    >
                                        View Audits
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </article>

            {/* Share Section */}
            <section className="share-section">
                <div className="container">
                    <div className="section-title text-center">
                        <h2>Share This Article</h2>
                        <div className="bar color-red"></div>
                    </div>
                    <div className="mt-3 text-center">
                        <button
                            className="btn share-btn share-twitter border-0"
                            onClick={shareOnTwitter}
                        >
                            <i className="fab fa-twitter"></i> Twitter
                        </button>
                        <button
                            className="btn share-btn share-linkedin border-0"
                            onClick={shareOnLinkedIn}
                        >
                            <i className="fab fa-linkedin"></i> LinkedIn
                        </button>
                    </div>
                </div>
            </section>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
                <section className="related-articles">
                    <div className="container">
                        <div className="section-title text-center">
                            <h2>Related Articles</h2>
                            <div className="bar color-red"></div>
                        </div>
                        <div className="row mt-4">
                            {relatedArticles.map((article, index) => (
                                <div className="col-md-6 mb-4" key={index}>
                                    <div className="related-card">
                                        <h4>
                                            <Link to={article.path}>{article.title}</Link>
                                        </h4>
                                        <p>{article.description}</p>
                                        <small className="text-muted">{article.readTime}</small>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

export default ArticleTemplate;
